import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  FileText,
  CreditCard,
  Receipt,
  PlusCircle,
  User,
  Settings,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  LogOut,
  TreePine,
  Building2,
  CalendarDays,
  ShieldCheck
} from 'lucide-react';
import { Button } from './ui/button';

const ClientSidebar = ({ mobileOpen, onMobileClose }) => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const { logout, user } = useAuth();

  const menuItems = [
    { path: '/client', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/client/loans', icon: CreditCard, label: 'My Loans' },
    { path: '/client/transactions', icon: Receipt, label: 'Transactions' },
    { path: '/client/statements', icon: FileText, label: 'Statements' },
    { path: '/client/fund-requests', icon: PlusCircle, label: 'Fund Requests' },
    { path: '/client/monthly-submissions', icon: CalendarDays, label: 'Monthly Docs' },
    { path: '/client/kyc', icon: ShieldCheck, label: 'KYC / FICA' },
    { path: '/client/profile', icon: User, label: 'Profile' },
    { path: '/client/settings', icon: Settings, label: 'Settings' },
    { path: '/client/support', icon: HelpCircle, label: 'Support' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onMobileClose}
          data-testid="client-sidebar-backdrop"
        />
      )}
      <aside
        className={`fixed left-0 top-0 h-full bg-[#0D3B66] text-white transition-all duration-300 z-50 w-64 ${
          collapsed ? 'lg:w-16' : 'lg:w-64'
        } ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      >
      {/* Header */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-[#1B5A8A]">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <img
              src="/logo-tree.svg"
              alt="Plane Tree Capital"
              className="h-6 w-6 brightness-0 invert"
            />
            <span className="font-bold text-lg font-['Manrope']">Client Portal</span>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:inline-flex text-white hover:bg-[#1B5A8A] h-8 w-8"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>

      {/* User Info */}
      {!collapsed && user && (
        <div className="px-4 py-3 border-b border-[#1B5A8A]">
          <p className="text-sm font-medium truncate">{user.first_name} {user.last_name}</p>
          <p className="text-xs text-[#7EC8E3] truncate">{user.email}</p>
          <span className="inline-block mt-1 px-2 py-0.5 bg-[#7EC8E3] text-[#0D3B66] text-xs font-semibold rounded">
            CLIENT
          </span>
        </div>
      )}

      {/* Navigation — clicking any link closes the mobile drawer */}
      <nav
        className="p-2 space-y-1 overflow-y-auto"
        style={{ maxHeight: 'calc(100vh - 180px)' }}
        onClick={(e) => { if (e.target.closest('a')) onMobileClose?.(); }}
      >
        {menuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              isActive(item.path, item.exact)
                ? 'bg-[#7EC8E3] text-[#0D3B66] font-medium'
                : 'text-gray-200 hover:bg-[#1B5A8A]'
            }`}
          >
            <item.icon className="h-5 w-5 flex-shrink-0" />
            {!collapsed && <span className="text-sm">{item.label}</span>}
          </Link>
        ))}
      </nav>

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 p-2 border-t border-[#1B5A8A]">
        {!collapsed && (
          <Link
            to="/login"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-300 hover:bg-[#1B5A8A] transition-colors mb-2"
          >
            <TreePine className="h-5 w-5" />
            <span className="text-sm">Main Portal</span>
          </Link>
        )}
        <button
          onClick={logout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-gray-300 hover:bg-red-600/20 hover:text-red-400 transition-colors"
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          {!collapsed && <span className="text-sm">Sign Out</span>}
        </button>
      </div>
      </aside>
    </>
  );
};

export default ClientSidebar;
